import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  SortAsc,
  SortDesc,
  Edit,
  Eye,
  Download,
  RefreshCw,
  MoreVertical,
  Calendar,
  FileText,
  X,
  AlertTriangle,
  Clock,
  User,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format, isValid, parseISO, differenceInDays } from "date-fns";
import {
  getEmployees,
  type Employee,
} from "@/services/employee.service";
import { useAuthStore } from "@/stores/authStore";
import { PERMISSIONS } from "@/types/permissions";
import { Agreement } from "@/types/models";
import { EditAgreementDialog } from "@/components/agreements/EditAgreementDialog";
import { ViewAgreementDialog } from "@/components/agreements/ViewAgreementDialog";

// Agreement status options
const AGREEMENT_STATUS_OPTIONS = [
  "All",
  "Active",
  "Expiring Soon",
  "Warning",
  "Expired",
];

export default function Agreements() {
  const { hasPermission } = useAuthStore();
  const navigate = useNavigate();


  // State management
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [allAgreements, setAllAgreements] = useState<Agreement[]>([]);
  const [allAgreementsHistory, setAllAgreementsHistory] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [viewMode, setViewMode] = useState<"all" | "grouped">("all");
  const [filterMonth, setFilterMonth] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "endDate", direction: "asc" });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingAgreement, setEditingAgreement] = useState<Agreement | null>(null);
  const [viewingAgreement, setViewingAgreement] = useState<Agreement | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [totalAgreements, setTotalAgreements] = useState(0);


  // Helper function to get agreement status
  const getAgreementStatus = (agreement: Agreement) => {
    const today = new Date();
    const startDate = new Date(agreement.startDate);
    const endDate = new Date(agreement.endDate);
    const daysUntilExpiry = differenceInDays(endDate, today);
    const daysUntilStart = differenceInDays(startDate, today);
    const notifyDays = parseInt(agreement.notifyPeriodInDays || '0', 10);

    // Check if agreement hasn't started yet
    if (daysUntilStart > 0) {
      return { status: 'upcoming', label: 'Upcoming', color: 'bg-blue-50 text-blue-700 border-blue-200' };
    }

    // Check if agreement has expired
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-700 border-red-200' };
    }

    // Check if agreement is expiring soon (within 7 days or notify period)
    if (daysUntilExpiry <= Math.min(7, notifyDays)) {
      return { status: 'expiring_soon', label: 'Expiring Soon', color: 'bg-orange-50 text-orange-700 border-orange-200' };
    }

    // Check if agreement is in warning period (within 30 days or notify period * 2)
    if (daysUntilExpiry <= Math.min(30, notifyDays * 2)) {
      return { status: 'warning', label: 'Warning', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    }

    // If none of the above, it's active
    return { status: 'active', label: 'Active', color: 'bg-green-50 text-green-700 border-green-200' };
  };

  // Helper function to process agreements from employee data
  const processAgreementsFromEmployees = (employees: Employee[]): Agreement[] => {
    const allAgreements: Agreement[] = [];

    employees.forEach(employee => {
      if (employee.agreementInfo && Array.isArray(employee.agreementInfo)) {
        // Sort agreements by start date (newest first) to get the latest
        const sortedAgreements = employee.agreementInfo.sort((a: any, b: any) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB.getTime() - dateA.getTime();
        });

        // Only take the first (latest) agreement for individual view
        if (sortedAgreements.length > 0) {
          const latestAgreement = sortedAgreements[0];
          allAgreements.push({
            _id: `${employee._id}-${latestAgreement.startDate}-${latestAgreement.endDate}`, // Generate unique ID
            startDate: latestAgreement.startDate,
            endDate: latestAgreement.endDate,
            periodInMonths: latestAgreement.periodInMonths || '',
            notifyPeriodInDays: latestAgreement.notifyPeriodInDays || '',
            note: latestAgreement.note || '',
            employee: {
              _id: employee._id || '',
              user: {
                firstName: employee.user?.firstName || '',
                lastName: employee.user?.lastName || '',
                email: employee.user?.email || '',
                employeeId: employee.employeeId || '',
              },
              department: employee.department,
            },
          });
        }
      }
    });

    return allAgreements;
  };

  // Helper function to process all agreements from employee data (for grouped view)
  const processAllAgreementsFromEmployees = (employees: Employee[]): Agreement[] => {
    const allAgreements: Agreement[] = [];

    employees.forEach(employee => {
      if (employee.agreementInfo && Array.isArray(employee.agreementInfo)) {
        // Sort agreements by start date (newest first) to show history
        const sortedAgreements = employee.agreementInfo.sort((a: any, b: any) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB.getTime() - dateA.getTime();
        });

        sortedAgreements.forEach((agreement, index) => {
          allAgreements.push({
            _id: `${employee._id}-${agreement.startDate}-${agreement.endDate}-${index}`, // Generate unique ID with index
            startDate: agreement.startDate,
            endDate: agreement.endDate,
            periodInMonths: agreement.periodInMonths || '',
            notifyPeriodInDays: agreement.notifyPeriodInDays || '',
            note: agreement.note || '',
            employee: {
              _id: employee._id || '',
              user: {
                firstName: employee.user?.firstName || '',
                lastName: employee.user?.lastName || '',
                email: employee.user?.email || '',
                employeeId: employee.employeeId || '',
              },
              department: employee.department,
            },
          });
        });
      }
    });

    return allAgreements;
  };

  // Helper function to group agreements by employee
  const groupAgreementsByEmployee = (agreements: Agreement[]) => {
    const grouped = agreements.reduce((acc, agreement) => {
      const employeeId = agreement.employee._id;
      if (!acc[employeeId]) {
        acc[employeeId] = {
          employee: agreement.employee,
          agreements: []
        };
      }
      acc[employeeId].agreements.push(agreement);
      return acc;
    }, {} as Record<string, { employee: Agreement['employee']; agreements: Agreement[] }>);

    return Object.values(grouped);
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const employeesData = await getEmployees();

        if (!Array.isArray(employeesData)) {
          setAllAgreements([]);
          setAllAgreementsHistory([]);
          return;
        }

        const latestAgreements = processAgreementsFromEmployees(employeesData);
        const allAgreementsData = processAllAgreementsFromEmployees(employeesData);

        setAllAgreements(latestAgreements);
        setAllAgreementsHistory(allAgreementsData);
        setLoading(false);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch agreement data.",
          variant: "destructive",
          duration: 1000,
        });
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter and sort data
  useEffect(() => {
    // Use appropriate data source based on view mode
    const sourceData = viewMode === "grouped" ? allAgreementsHistory : allAgreements;

    let filteredAgreements = sourceData.filter((agreement: Agreement) => {
      const matchesSearch =
        !searchTerm ||
        (agreement.employee.user.firstName &&
          agreement.employee.user.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (agreement.employee.user.lastName &&
          agreement.employee.user.lastName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (agreement.employee.user.email &&
          agreement.employee.user.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (agreement.employee.user.employeeId &&
          agreement.employee.user.employeeId
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));

      const statusInfo = getAgreementStatus(agreement);
      const matchesStatus =
        selectedStatus === "All" || statusInfo.status === selectedStatus.toLowerCase().replace(' ', '_');

      // Month filtering - show only agreements that expire in the selected month
      let matchesMonthFilter = true;
      if (filterMonth) {
        const selectedDate = new Date(filterMonth);
        const agreementEndDate = new Date(agreement.endDate);

        // Get the first and last day of the selected month
        const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

        // Check if agreement expires in the selected month
        const expiresInSelectedMonth = agreementEndDate >= monthStart && agreementEndDate <= monthEnd;

        matchesMonthFilter = expiresInSelectedMonth;
      }

      return matchesSearch && matchesStatus && matchesMonthFilter;
    });

    // Sort data
    filteredAgreements.sort((a: Agreement, b: Agreement) => {
      let aValue: any = a[sortConfig.key as keyof Agreement];
      let bValue: any = b[sortConfig.key as keyof Agreement];

      // Special handling for dates
      if (sortConfig.key === 'endDate' || sortConfig.key === 'startDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortConfig.direction === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // Pagination
    setTotalAgreements(filteredAgreements.length);
    const totalPages = Math.ceil(filteredAgreements.length / pageSize);
    const newCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    const startIndex = (newCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedAgreements = filteredAgreements.slice(startIndex, endIndex);

    setAgreements(paginatedAgreements);

    if (currentPage !== newCurrentPage) {
      setCurrentPage(newCurrentPage);
    }
  }, [
    allAgreements,
    allAgreementsHistory,
    searchTerm,
    selectedStatus,
    filterMonth,
    sortConfig,
    currentPage,
    pageSize,
    viewMode,
  ]);

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Actions handlers
  const handleView = (agreement: Agreement) => {
    setViewingAgreement(agreement);
    setIsViewDialogOpen(true);
  };

  const handleEdit = (agreement: Agreement) => {
    setEditingAgreement(agreement);
    setIsEditDialogOpen(true);
  };

  const handleViewProfile = (agreement: Agreement) => {
    navigate(`/employee/${agreement.employee._id}`);
  };

  const handleEditSuccess = async () => {
    // Refresh the agreement list after successful edit
    try {
      const employeesData = await getEmployees();
      const latestAgreements = processAgreementsFromEmployees(employeesData);
      const allAgreementsData = processAllAgreementsFromEmployees(employeesData);

      setAllAgreements(latestAgreements);
      setAllAgreementsHistory(allAgreementsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh data after edit",
        variant: "destructive",
        duration: 1000,
      });
    }
  };


  // Refresh data handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const employeesData = await getEmployees();
      const latestAgreements = processAgreementsFromEmployees(employeesData);
      const allAgreementsData = processAllAgreementsFromEmployees(employeesData);

      setAllAgreements(latestAgreements);
      setAllAgreementsHistory(allAgreementsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
        duration: 1000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const data = await getEmployees();
      const processedAgreements = viewMode === "grouped"
        ? processAllAgreementsFromEmployees(data)
        : processAgreementsFromEmployees(data);

      // Filter data for export based on current view
      const filteredData = processedAgreements.filter((agreement: Agreement) => {
        const matchesSearch =
          !searchTerm ||
          agreement.employee.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agreement.employee.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agreement.employee.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agreement.employee.user.employeeId.toLowerCase().includes(searchTerm.toLowerCase());

        const statusInfo = getAgreementStatus(agreement);
        const matchesStatus =
          selectedStatus === "All" || statusInfo.status === selectedStatus.toLowerCase().replace(' ', '_');

        return matchesSearch && matchesStatus;
      });

      // Sort by end date
      filteredData.sort((a, b) => {
        const dateA = new Date(a.endDate).getTime();
        const dateB = new Date(b.endDate).getTime();
        return dateA - dateB;
      });

      // Create CSV content
      const csvContent = [
        // Header
        [
          "Employee ID",
          "Employee Name",
          "Email",
          "Department",
          "Start Date",
          "End Date",
          "Period (Months)",
          "Notify Days",
          "Status",
          "Note"
        ].join(","),
        // Data rows
        ...filteredData.map(agreement => {
          const statusInfo = getAgreementStatus(agreement);
          const departmentName = typeof agreement.employee.department === "object"
            ? agreement.employee.department?.name || "N/A"
            : agreement.employee.department || "N/A";

          return [
            agreement.employee.user.employeeId,
            `"${agreement.employee.user.firstName} ${agreement.employee.user.lastName}"`,
            agreement.employee.user.email,
            `"${departmentName}"`,
            format(new Date(agreement.startDate), "yyyy-MM-dd"),
            format(new Date(agreement.endDate), "yyyy-MM-dd"),
            agreement.periodInMonths,
            agreement.notifyPeriodInDays,
            statusInfo.label,
            `"${agreement.note || ""}"`
          ].join(",");
        })
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `agreements_${format(new Date(), "yyyy-MM-dd")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Success",
        description: "Agreements data has been exported",
        duration: 1000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive",
        duration: 1000,
      });
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalAgreements / pageSize);


  return (
    <div className="space-y-4 p-4 pt-1 pb-10">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Agreement Management
            </h1>
          </div>
        </div>

        {/* Stats Cards */}
        {allAgreements.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Total Agreements
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allAgreements.length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Active Agreements
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allAgreements.filter(a => getAgreementStatus(a).status === 'active').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-orange-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Expiring Soon
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allAgreements.filter(a => getAgreementStatus(a).status === 'expiring_soon').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
                    <X className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Expired
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allAgreements.filter(a => getAgreementStatus(a).status === 'expired').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Card */}
        <Card className="border border-gray-200 shadow-sm rounded-lg mb-6 bg-white">
          <CardHeader className="pb-2"> </CardHeader>

          <CardContent className="pt-0">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by employee name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1 h-6 w-6"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Status Filter */}
              <Select
                value={selectedStatus}
                onValueChange={setSelectedStatus}
              >
                <SelectTrigger className="h-10 w-32 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {AGREEMENT_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Month Filter */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="month"
                  placeholder="Select Month"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  onClick={(e) => {
                    e.currentTarget.focus();
                    if (e.currentTarget.showPicker) {
                      e.currentTarget.showPicker();
                    }
                  }}
                  onFocus={(e) => {
                    if (e.currentTarget.showPicker) {
                      e.currentTarget.showPicker();
                    }
                  }}
                  className="pl-10 h-10 w-40 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white cursor-pointer"
                />
              </div>

              {/* View Mode */}
              <Select
                value={viewMode}
                onValueChange={(value: "all" | "grouped") => setViewMode(value)}
              >
                <SelectTrigger className="h-10 w-40 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <FileText className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Select view mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Individual Records
                  </SelectItem>
                  <SelectItem value="grouped">
                    Employee History
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedStatus("All");
                    setFilterMonth("");
                  }}
                  className="h-10 w-10"
                  title="Clear Filters"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleExport}
                  size="icon"
                  className="h-10 w-10"
                  title="Download CSV"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agreements List */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {viewMode === "grouped" ? "Employee Agreement History" : "Agreement Records"}
                </CardTitle>

              </div>
              <div className="flex items-center space-x-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(v) => setPageSize(Number(v))}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Rows per page" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50].map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size} rows
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""
                            }`}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh Data</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("employee.user.firstName")}
                    >
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Employee</span>
                        {sortConfig.key === "employee.user.firstName" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Department</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("startDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Start Date</span>
                        {sortConfig.key === "startDate" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("endDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">End Date</span>
                        {sortConfig.key === "endDate" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Period</TableHead>
                    <TableHead className="text-xs sm:text-sm">Status</TableHead>
                    <TableHead className="text-right text-xs sm:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          <Skeleton className="h-5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-20 rounded-md" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-8 w-8 ml-auto" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : isRefreshing ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">
                            Loading agreements...
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : agreements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <span className="text-sm text-muted-foreground">
                            No agreements found
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchTerm("");
                              setSelectedStatus("All");
                              setFilterMonth("");
                            }}
                          >
                            Clear Filters
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : viewMode === "grouped" ? (
                    // Grouped view - show employee groups
                    (() => {
                      const groupedData = groupAgreementsByEmployee(agreements);
                      return groupedData.map((group) => (
                        <React.Fragment key={group.employee._id}>
                          {/* Employee header row */}
                          <TableRow className="bg-gray-50">
                            <TableCell colSpan={7} className="font-semibold text-gray-900 py-3">
                              <div className="flex items-center gap-3">
                                <User className="h-4 w-4" />
                                <span>
                                  {group.employee.user.firstName} {group.employee.user.lastName}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {group.agreements.length} agreement{group.agreements.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Agreement rows for this employee */}
                          {group.agreements.map((agreement, index) => {
                            const statusInfo = getAgreementStatus(agreement);
                            const departmentName = typeof agreement.employee.department === "object"
                              ? agreement.employee.department?.name || "N/A"
                              : agreement.employee.department || "N/A";

                            return (
                              <TableRow key={agreement._id} className="border-l-4 border-l-blue-200">
                                <TableCell className="font-medium text-xs sm:text-sm pl-8">
                                  <div className="text-gray-500 text-xs">
                                    Agreement #{index + 1}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-50 text-blue-700 text-xs"
                                  >
                                    {departmentName}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs sm:text-sm">
                                  {isValid(parseISO(String(agreement.startDate)))
                                    ? format(parseISO(String(agreement.startDate)), "dd/MM/yyyy")
                                    : "N/A"}
                                </TableCell>
                                <TableCell className="text-xs sm:text-sm">
                                  {isValid(parseISO(String(agreement.endDate)))
                                    ? format(parseISO(String(agreement.endDate)), "dd/MM/yyyy")
                                    : "N/A"}
                                </TableCell>
                                <TableCell className="text-xs sm:text-sm">
                                  {agreement.periodInMonths || "N/A"} months
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${statusInfo.color}`}
                                  >
                                    {statusInfo.label}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <DropdownMenu>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 sm:h-9 sm:w-9"
                                            >
                                              <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                        </TooltipTrigger>
                                        <TooltipContent>Actions</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-[180px]"
                                    >
                                      {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                                        <DropdownMenuItem
                                          onClick={() => handleView(agreement)}
                                        >
                                          <Eye className="mr-2 h-4 w-4" />
                                          View Details
                                        </DropdownMenuItem>
                                      )}
                                      {hasPermission(PERMISSIONS.EMPLOYEES_EDIT) && (
                                        <DropdownMenuItem
                                          onClick={() => handleEdit(agreement)}
                                        >
                                          <Edit className="mr-2 h-4 w-4" />
                                          Edit Agreement
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ));
                    })()
                  ) : (
                    // Regular view - show all agreements
                    agreements.map((agreement) => {
                      const statusInfo = getAgreementStatus(agreement);
                      const departmentName = typeof agreement.employee.department === "object"
                        ? agreement.employee.department?.name || "N/A"
                        : agreement.employee.department || "N/A";

                      return (
                        <TableRow key={agreement._id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            <div>
                              <div className="font-semibold">
                                {agreement.employee.user.firstName} {agreement.employee.user.lastName}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {agreement.employee.user.employeeId}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {agreement.employee.user.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 text-xs"
                            >
                              {departmentName}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {isValid(parseISO(String(agreement.startDate)))
                              ? format(parseISO(String(agreement.startDate)), "dd/MM/yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {isValid(parseISO(String(agreement.endDate)))
                              ? format(parseISO(String(agreement.endDate)), "dd/MM/yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {agreement.periodInMonths || "N/A"} months
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`text-xs ${statusInfo.color}`}
                            >
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 sm:h-9 sm:w-9"
                                      >
                                        <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent>Actions</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <DropdownMenuContent
                                align="end"
                                className="w-[180px]"
                              >
                                {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                                  <DropdownMenuItem
                                    onClick={() => handleView(agreement)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </DropdownMenuItem>
                                )}
                                {hasPermission(PERMISSIONS.AGREEMENT_EDIT) && (
                                  <DropdownMenuItem
                                    onClick={() => handleViewProfile(agreement)}
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    View Profile
                                  </DropdownMenuItem>
                                )}
                                {hasPermission(PERMISSIONS.AGREEMENT_EDIT) && (
                                  <DropdownMenuItem
                                    onClick={() => handleEdit(agreement)}
                                  >
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Agreement
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {!loading && !isRefreshing && agreements.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalAgreements)} of{" "}
                  {totalAgreements} entries
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="h-8 px-3 text-sm"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="default"
                    className="h-8 w-8 p-0 text-sm bg-[#000000] hover:bg-[#000000] text-white"
                    disabled
                  >
                    {currentPage}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-8 px-3 text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Agreement Dialog */}
      <ViewAgreementDialog
        agreement={viewingAgreement}
        open={isViewDialogOpen}
        onOpenChange={(open) => {
          setIsViewDialogOpen(open);
          if (!open) {
            setViewingAgreement(null);
          }
        }}
      />

      {/* Edit Agreement Dialog */}
      <EditAgreementDialog
        agreement={editingAgreement}
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingAgreement(null);
          }
        }}
        onSuccess={handleEditSuccess}
      />

    </div>
  );
}
